import {
  CLAUDE_BRAND_TEXT_CLASS,
  ClaudeIcon,
  OpenAIIcon,
} from "@/components/app/brand-icons";
import type { McpAppDirectory } from "@/config/mcp-app-directories";
import { cn } from "@/lib/utils";

/**
 * A `Record` over the id union rather than a ternary, so adding a directory to
 * the registry fails type-check here instead of silently drawing the wrong
 * vendor's logo.
 */
const MARKS: Record<
  McpAppDirectory["id"],
  (props: { className?: string }) => React.ReactNode
> = {
  chatgpt: OpenAIIcon,
  claude: ClaudeIcon,
};

/**
 * Only Claude has a colour of its own to opt into; OpenAI's mark is monochrome,
 * so it keeps whatever text colour the caller set either way.
 */
const BRAND_CLASSES: Partial<Record<McpAppDirectory["id"], string>> = {
  claude: CLAUDE_BRAND_TEXT_CLASS,
};

/**
 * The directory's own mark. Kept out of the registry because that module is
 * plain data imported by server code, and a React component in it would drag
 * JSX into every consumer.
 */
export function DirectoryIcon({
  id,
  className,
  brand = false,
}: {
  id: McpAppDirectory["id"];
  className?: string;
  /**
   * Draw the mark in the vendor's own colour. For neutral surfaces only; the
   * hero and the install button sit on blue and on dark, where the mark is
   * white.
   */
  brand?: boolean;
}) {
  const Mark = MARKS[id];

  // `cn` resolves the text-colour conflict, so a caller's `text-*` class stays
  // the default and the brand colour wins when it is asked for.
  return <Mark className={cn(className, brand && BRAND_CLASSES[id])} />;
}
